import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicPlan, fetchPublicPlans } from '../../api/site';
import { PLAN_FEATURE_ORDER, assistantLine, featureLine } from '../../shared/planFeatureLines';
import { useMediaQuery } from '../../shared/media';
import { TRIAL_DAYS, annualSaving, fmtMoney, planLimitLines } from './planCopy';

/**
 * Pricing (screen 02), rendered from the live plan catalogue — the same rows
 * the product enforces. Nothing here is hand-maintained but the limit lines
 * (`planCopy.ts`): a plan edit in the platform console changes this page on
 * the next load.
 *
 * R13, the design's card: name and flag, tagline, price, the button, the
 * limits, a rule, then every plan-gated feature ✓ or –. Annual shows each
 * plan's real annual price over twelve and its own saving (§4), never a
 * fixed 20%.
 */

/**
 * How many cards stand side by side.
 *
 * Five is the ceiling: past that the cards are narrower than the price they
 * carry, so a sixth plan wraps onto a second row rather than shrinking the
 * first five. Below five the grid simply matches the catalogue, so a
 * three-plan install does not render two empty columns.
 */
const MAX_ACROSS = 5;

/**
 * Below this the grid becomes a snap-scroll carousel (A4 option (a)).
 * Its own breakpoint rather than one of the three shared ones: it is the width
 * at which the cards already stacked, and the CSS that draws the carousel
 * lives at the same number (`site.css`).
 */
const CAROUSEL = '(max-width: 620px)';

export default function PricingPage() {
  const [plans, setPlans] = useState<PublicPlan[] | null>(null);
  const [error, setError] = useState('');
  const [annual, setAnnual] = useState(false);
  // The trial's length is a platform rule (Platform › Rules), read with the
  // catalogue; the code's number stands in until the server has answered.
  const [trialDays, setTrialDays] = useState(TRIAL_DAYS);

  useEffect(() => {
    fetchPublicPlans()
      .then((r) => { setPlans(r.plans); if (r.rules) setTrialDays(r.rules.firstTrialDays); })
      .catch((e) => setError(e.message || 'Could not load plans'));
  }, []);

  const saving = annualSaving(plans || []);
  // Said from the catalogue, not assumed: one currency across the plans, or none named.
  const currencies = [...new Set((plans || []).map((p) => p.currency.toUpperCase()))];
  const columns = Math.min(Math.max(plans?.length || 0, 1), MAX_ACROSS);
  const fiveUp = columns === MAX_ACROSS;

  /**
   * On a phone the cards are a carousel, so the page needs a "you are here":
   * five stacked cards bury the popular plan, and a row that scrolls sideways
   * with no dots does not say there is more. Both the dots and the opening
   * scroll position only exist while the carousel query matches, so a desktop
   * renders exactly the grid.
   */
  const carousel = useMediaQuery(CAROUSEL);
  const grid = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(0);

  const cardsOf = (el: HTMLElement) =>
    [...el.children].filter((c) => c.classList.contains('plan-card')) as HTMLElement[];

  const scrollToCard = (i: number) => {
    const el = grid.current;
    const card = el && cardsOf(el)[i];
    if (el && card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = grid.current;
    if (!carousel || !el || !plans?.length) return;
    // Open on the plan being recommended, not on the cheapest one.
    const start = Math.max(0, plans.findIndex((p) => p.popular));
    const cards = cardsOf(el);
    if (cards[start]) el.scrollLeft = cards[start].offsetLeft - el.offsetLeft;
    setShown(start);
    const onScroll = () => {
      let best = 0;
      let least = Infinity;
      cardsOf(el).forEach((c, i) => {
        const d = Math.abs(c.offsetLeft - el.offsetLeft - el.scrollLeft);
        if (d < least) { least = d; best = i; }
      });
      setShown(best);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [carousel, plans]);

  return (
    <section className={`site-page plan-section${fiveUp ? ' wide' : ''}`} data-frame="column">
      <div className="pricing-head">
        <h1 className="site-h1">Public pricing. No sales call.</h1>
        <p className="site-sub">
          {trialDays > 0 ? `${trialDays} days free on every paid plan.` : 'Every paid plan is charged from the day you sign up.'} The Free plan needs no card.
        </p>
        <div className="fs-seg site-seg" role="group" aria-label="Billing period">
          <button type="button" className={annual ? '' : 'on'} aria-pressed={!annual} onClick={() => setAnnual(false)}>
            Monthly
          </button>
          <button type="button" className={annual ? 'on' : ''} aria-pressed={annual} onClick={() => setAnnual(true)}>
            Annual{saving && <> · {saving}</>}
          </button>
        </div>
      </div>

      {error && <div className="site-err" style={{ marginTop: 24 }}>{error}</div>}
      {!plans && !error && <div className="site-empty">Loading plans…</div>}
      {plans && (
        <div
          ref={grid}
          className={`plan-grid${fiveUp ? ' five-up' : ''}`}
          style={{ '--plan-cols': columns } as CSSProperties}
        >
          {plans.map((p) => {
            const monthly = p.price_monthly_cents;
            const onAnnual = annual && monthly !== null && p.price_annual_cents !== null;
            const shownCents = onAnnual ? (p.price_annual_cents as number) / 12 : (monthly ?? 0);
            return (
              <div key={p.key} className={`plan-card${p.popular ? ' popular' : ''}`}>
                <div className="plan-head">
                  <h3>{p.name}</h3>
                  {p.popular && <span className="flag">Most popular</span>}
                </div>
                <div className="tagline">{p.tagline}</div>
                <div className="price">
                  <span className="amt">{fmtMoney(Math.round(shownCents), p.currency)}</span>
                  <span className="per">/mo</span>
                </div>
                <div className="annual-note">
                  {onAnnual && `Billed annually at ${fmtMoney(p.price_annual_cents as number, p.currency)}`}
                  {monthly !== null && !annual && p.price_annual_cents !== null && p.annual_discount_pct > 0 &&
                    `Or ${p.annual_discount_pct}% less, billed annually`}
                </div>
                {/* The plan travels with the click: signup starts on the
                    plan they picked here, not on Free with a chore left
                    over. */}
                <Link
                  className={`fs-btn ${p.popular ? 'primary' : 'neutral'}`}
                  to={monthly === null
                    ? '/signup'
                    : `/signup?plan=${encodeURIComponent(p.key)}&interval=${onAnnual ? 'year' : 'month'}`}
                >
                  {monthly === null ? 'Start free' : trialDays > 0 ? `Start ${trialDays}-day free trial` : `Start on ${p.name}`}
                </Link>
                <ul className="plan-limits">
                  {planLimitLines(p).map((line) => <li key={line}>{line}</li>)}
                </ul>
                <div className="plan-rule" aria-hidden="true" />
                {/* Every plan-gated feature, in the shared order the internal
                    picker uses — greyed when the plan lacks it, never hidden,
                    so the ladder reads from any card. */}
                <ul className="plan-features">
                  {PLAN_FEATURE_ORDER.flatMap((key) => {
                    const f = featureLine(key, p.features, p.limits);
                    const a = key === 'aiBuilder' ? assistantLine(p.features, p.limits) : null;
                    return [
                      <li key={key} className={f.has ? '' : 'no'}>{f.text}</li>,
                      ...(a ? [<li key={`${key}-assistant`} className={a.has ? '' : 'no'}>{a.text}</li>] : []),
                    ];
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {carousel && plans && plans.length > 1 && (
        <div className="plan-dots" aria-label="Plans">
          {plans.map((p, i) => (
            <button
              key={p.key} type="button" className={i === shown ? 'on' : ''}
              aria-label={p.name} aria-current={i === shown}
              onClick={() => scrollToCard(i)}
            />
          ))}
        </div>
      )}
      <p className="plan-terms">
        Sign up on whichever plan you want.{' '}
        {trialDays > 0 ? (
          <>
            Every paid plan starts with a {trialDays}-day free trial, and we take a card when it
            starts, so nothing is interrupted when it ends — you are charged on day {trialDays + 1} unless
            you cancel from Billing first.
          </>
        ) : (
          <>Every paid plan takes a card at signup and is charged from that day; cancel from Billing any time.</>
        )}{' '}
        The Free plan needs no card at all.{currencies.length === 1 && <> Prices are in {currencies[0]}.</>}
      </p>
    </section>
  );
}
